import { useCallback, useEffect, useMemo, useState } from "react"

import { useCollectionsQuery, queryClient } from "@Utils/queries"

/**
 * @template  {string} T
 * @template {string} [K='collections']
 * @param  {T} collectionsURL
 * @param {{key: K, sendSiteId: boolean}} options
 */

export default function hookCollectionsStoreFactory(initialCollectionsURL, { sendSiteId = true, key = 'collections' }) {
  return ({ fetch, siteSelected, collectionsURL: collectionsURLExtend }) => {
    const collectionsURL = useMemo(() => collectionsURLExtend || initialCollectionsURL, [collectionsURLExtend])

    const { items, total, actives, hasNextPage, list, reset, setQuery, setItems } = useCollectionsState(collectionsURL)

    useEffect(() => () => {
      reset()
    }, [reset])

    return useMemo(() => {
      const store = {
        collectionsURL,
        items, reset, setItems, list, actives, hasNextPage, total,
        showing: items.length,

        useLoad: ({ autoRefresh, ...params } = {}) => {
          const query = useCollectionsQuery({
            params,
            collectionsURL,
            siteSelected,
            sendSiteId,
            fetch,
            autoRefresh,
          })
          setQuery(query)
          const { refetch } = query

          return refetch
        },
      }

      /** @type {{[x in K]: store}} */
      const result = {
        [key]: store,
      }
      return result
    }, [collectionsURL, items, reset, list, actives, hasNextPage, total, siteSelected, fetch, setQuery, setItems])
  }
}

function useCollectionsState(collectionsURL) {
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [actives, setActives] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [query, setQuery] = useState({})

  const { refetch: list, data } = query

  const reset = useCallback(() => {
    setActives(0)
    setHasNextPage(0)
    setItems([])
    setTotal(0)
    queryClient.cancelQueries({
      queryKey: [collectionsURL],
      exact: false,
    })
    queryClient.removeQueries({
      queryKey: [collectionsURL],
      exact: false,
    })
  }, [collectionsURL])

  useEffect(() => {
    if (data) {
      const { rows, total } = data
      setItems(rows)
      setTotal(total)
      setActives(rows.reduce((sum, { active }) => active ? sum + 1 : sum, 0))
      setHasNextPage(total > rows.length)
    }
  }, [data])

  return {
    items, reset, list, actives, hasNextPage, total, setItems,
    query, setQuery,
  }
}
